/**
 * Socket Service - Socket.IO connection manager for real-time chat
 * Mirrors the mobile app's socket implementation pattern
 */

import { io } from 'socket.io-client';

// Socket endpoint from environment
const SOCKET_URL = process.env.REACT_APP_CHAT_SOCKET_URL || 'http://68.178.162.134:5001';

/**
 * Socket Service Class
 * Manages Socket.IO connection lifecycle and event handling
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.eventListeners = new Map();
    this.heartbeatInterval = null;
    this.connectionPromise = null;
  }

  /**
   * Initialize socket connection with chat token
   * @param {string} chatToken - JWT token for authentication
   * @param {Object} user - Current user object
   * @returns {Promise<Socket>} Connected socket instance
   */
  connect(chatToken, user) {
    // Return existing connection promise if connecting
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Return existing socket if already connected
    if (this.socket?.connected) {
      return Promise.resolve(this.socket);
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        // Disconnect existing socket if any
        if (this.socket) {
          this.socket.disconnect();
          this.socket = null;
        }

        console.log('🔌 Connecting to chat server:', SOCKET_URL);

        // Create socket connection with auth
        this.socket = io(SOCKET_URL, {
          auth: { token: chatToken }, // JWT in auth object (no Bearer prefix)
          transports: ['websocket', 'polling'], // WebSocket preferred, polling fallback
          autoConnect: true,
          reconnection: true,
          reconnectionDelay: 5000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: this.maxReconnectAttempts,
          timeout: 10000,
          forceNew: true
        });

        // Connection event handlers
        this.socket.on('connect', () => {
          console.log('✅ Socket connected:', this.socket.id);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.connectionPromise = null;
          
          // Start heartbeat for presence
          this.startHeartbeat();
          
          resolve(this.socket);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('❌ Socket disconnected:', reason);
          this.isConnected = false;
          this.stopHeartbeat();
          
          // Emit custom event for UI updates
          this.emit('connectionStatusChange', { connected: false, reason });
        });

        this.socket.on('connect_error', (error) => {
          console.error('🚫 Socket connection error:', error.message);
          this.reconnectAttempts++;
          
          // Check if it's an authentication error
          if (error.message === 'Invalid token' || error.message === 'Authentication required') {
            console.error('🔒 Authentication failed - token expired or invalid');
            this.emit('authenticationError', { 
              message: error.message,
              isTokenExpired: true 
            });
            this.connectionPromise = null;
            reject(new Error('Authentication failed: ' + error.message));
            return;
          }
          
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.connectionPromise = null;
            reject(new Error('Failed to connect to chat server after maximum attempts'));
          }
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`🔄 Socket reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          
          // Emit custom event for UI updates
          this.emit('connectionStatusChange', { connected: true, reconnected: true });
        });

        this.socket.on('reconnect_failed', () => {
          console.error('❌ Socket reconnection failed after all attempts');
          this.isConnected = false;
          
          // Attempt manual reconnection after delay
          setTimeout(() => {
            if (!this.socket?.connected && chatToken) {
              console.log('🔄 Attempting manual reconnection...');
              this.socket.connect();
            }
          }, 10000);
        });

        this.socket.on('error', (error) => {
          console.error('🚫 Socket error:', error);
          this.emit('socketError', error);
        });

      } catch (error) {
        console.error('🚫 Failed to initialize socket:', error);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect socket and cleanup
   */
  disconnect() {
    console.log('🔌 Disconnecting socket...');
    
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.isConnected = false;
    this.connectionPromise = null;
    this.eventListeners.clear();
  }

  /**
   * Start heartbeat interval for presence updates
   */
  startHeartbeat() {
    this.stopHeartbeat(); // Clear existing interval
    
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('user:update-presence', {}, (response) => {
          if (response?.error) {
            console.warn('Heartbeat failed:', response.error);
          }
        });
      }
    }, 30000); // 30 second heartbeat
  }

  /**
   * Stop heartbeat interval
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Get connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.socket?.connected || false;
  }

  /**
   * Get socket instance
   * @returns {Socket|null} Socket instance or null
   */
  getSocket() {
    return this.socket;
  }

  // ==========================================
  // EVENT EMITTERS (Client → Server)
  // ==========================================

  /**
   * Emit event with optional callback
   * @param {string} event - Event name
   * @param {*} data - Event payload
   * @param {Function} callback - Optional callback
   */
  emit(event, data, callback) {
    if (!this.socket?.connected) {
      console.debug(`[SocketService] Skipping '${event}': socket not connected`);
      if (callback) callback({ error: 'Socket not connected' });
      return;
    }
    
    if (callback) {
      this.socket.emit(event, data, callback);
    } else {
      this.socket.emit(event, data);
    }
  }

  /**
   * Send a chat message
   * @param {Object} messageData - Message payload
   * @param {Function} callback - Response callback
   */
  sendMessage(messageData, callback) {
    this.emit('chat:send', messageData, callback);
  }

  /**
   * Mark message as delivered
   * @param {Object} data - { messageId, chatType }
   * @param {Function} callback - Response callback
   */
  markAsDelivered(data, callback) {
    this.emit('chat:mark-delivered', data, callback);
  }

  /**
   * Mark message(s) as read
   * @param {Object} data - { messageId?, chatId?, chatType }
   * @param {Function} callback - Response callback
   */
  markAsRead(data, callback) {
    this.emit('chat:mark-read', data, callback);
  }

  /**
   * Batch mark multiple specific messages as read in one call
   * @param {Object} data - { messageIds: string[], chatId, chatType }
   * @param {Function} callback - Response callback
   */
  batchMarkRead(data, callback) {
    this.emit('chat:batch-mark-read', data, callback);
  }

  /**
   * Pin a message
   * @param {Object} data - { messageId, chatId, chatType, userId }
   * @param {Function} callback - Response callback
   */
  pinMessage(data, callback) {
    this.emit('chat:pin-message', data, callback);
  }

  /**
   * Unpin a message
   * @param {Object} data - { messageId, chatId, chatType, userId }
   * @param {Function} callback - Response callback
   */
  unpinMessage(data, callback) {
    this.emit('chat:unpin-message', data, callback);
  }

  /**
   * Delete a message
   * @param {Object} data - { messageId, chatId, chatType }
   * @param {Function} callback - Response callback
   */
  deleteMessage(data, callback) {
    this.emit('chat:delete-message', data, callback);
  }

  /**
   * Like/unlike a message
   * @param {Object} data - { messageId, chatId, chatType }
   * @param {Function} callback - Response callback
   */
  likeMessage(data, callback) {
    this.emit('chat:toggle-like', data, callback);
  }

  /**
   * Forward message to another chat
   * @param {Object} data - Forward message payload
   * @param {Function} callback - Response callback
   */
  forwardMessage(data, callback) {
    this.emit('chat:forward-message', data, callback);
  }

  /**
   * Get unread message counts
   * @param {Function} callback - Response callback
   */
  requestUnreadCounts(callback) {
    this.emit('chat:get-unread-count', {}, callback);
  }

  /**
   * Get online status of specific users
   * @param {number[]} userIds - Array of user IDs
   * @param {Function} callback - Response callback
   */
  getOnlineStatus(userIds, callback) {
    this.emit('user:get-online-status', { userIds }, callback);
  }

  /**
   * Get all online users
   * @param {Function} callback - Response callback
   */
  getAllOnlineUsers(callback) {
    this.emit('user:get-all-online', {}, callback);
  }

  /**
   * Get pinned messages for a chat
   * @param {Object} data - { chatId, chatType }
   * @param {Function} callback - Response callback
   */
  getPinnedMessages(data, callback) {
    this.emit('chat:get-pinned-messages', data, callback);
  }

  /**
   * Join a group room for real-time updates
   * @param {string} groupId - Group identifier
   */
  joinGroupRoom(groupId) {
    this.emit('group:join', groupId);
  }

  /**
   * Leave a group room
   * @param {string} groupId - Group identifier
   */
  leaveGroupRoom(groupId) {
    this.emit('group:leave', groupId);
  }

  // ==========================================
  // EVENT LISTENERS (Server → Client)
  // ==========================================

  /**
   * Register event listener
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  on(event, handler) {
    if (!this.socket) {
      console.warn(`Cannot listen to '${event}': Socket not initialized`);
      return;
    }
    
    this.socket.on(event, handler);
    
    // Track listeners for cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(handler);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} handler - Event handler to remove
   */
  off(event, handler) {
    if (this.socket) {
      this.socket.off(event, handler);
    }
    
    // Remove from tracking
    if (this.eventListeners.has(event)) {
      const handlers = this.eventListeners.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Remove all listeners for an event
   * @param {string} event - Event name
   */
  removeAllListeners(event) {
    if (this.socket) {
      this.socket.removeAllListeners(event);
    }
    this.eventListeners.delete(event);
  }

  // ==========================================
  // CONVENIENCE LISTENER METHODS
  // ==========================================

  /**
   * Listen for new messages
   * @param {Function} handler - Message handler
   */
  onReceiveMessage(handler) {
    this.on('chat:receive', handler);
  }

  /**
   * Listen for message status updates
   * @param {Function} handler - Status update handler
   */
  onMessageStatusUpdate(handler) {
    this.on('chat:status-update', handler);
  }

  /**
   * Listen for bulk message status updates
   * @param {Function} handler - Bulk status handler
   */
  onMessageStatusBulkUpdate(handler) {
    this.on('chat:bulk-status-update', handler);
  }

  /**
   * Listen for message pinned events
   * @param {Function} handler - Pin handler
   */
  onMessagePinned(handler) {
    this.on('chat:message-pinned', handler);
  }

  /**
   * Listen for message unpinned events
   * @param {Function} handler - Unpin handler
   */
  onMessageUnpinned(handler) {
    this.on('chat:message-unpinned', handler);
  }

  /**
   * Listen for message deleted events
   * @param {Function} handler - Delete handler
   */
  onMessageDeleted(handler) {
    this.on('chat:message-deleted', handler);
  }

  /**
   * Listen for message liked events
   * @param {Function} handler - Like handler
   */
  onMessageLiked(handler) {
    this.on('chat:message-liked', handler);
  }

  /**
   * Listen for user status changes (online/offline)
   * @param {Function} handler - Status change handler
   */
  onUserStatusChange(handler) {
    this.on('user:status-change', handler);
  }

  /**
   * Listen for unread count updates
   * @param {Function} handler - Unread count handler
   */
  onUnreadCountUpdate(handler) {
    this.on('chat:unread-count-update', handler);
  }

  /**
   * Listen for group created events
   * @param {Function} handler - Group created handler
   */
  onGroupCreated(handler) {
    this.on('group:created', handler);
  }

  /**
   * Listen for group updated events
   * @param {Function} handler - Group updated handler
   */
  onGroupUpdated(handler) {
    this.on('group:updated', handler);
  }

  /**
   * Listen for group deleted events
   * @param {Function} handler - Group deleted handler
   */
  onGroupDeleted(handler) {
    this.on('group:deleted', handler);
  }

  /**
   * Listen for typing events
   * @param {Function} handler - Typing handler
   */
  onTyping(handler) {
    this.on('typing', handler);
  }

  /**
   * Listen for stop typing events
   * @param {Function} handler - Stop typing handler
   */
  onStopTyping(handler) {
    this.on('stopTyping', handler);
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
